import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, AlertCircle, ShieldCheck } from "lucide-react";

/**
 * Handles return from Emergent Google OAuth.
 * URL fragment looks like: /dashboard#session_id=xxx OR /security?mode=link#session_id=xxx
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { exchangeGoogleSession, linkGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("Missing session_id");
      setStatus("error");
      return;
    }
    const sessionId = m[1];
    const params = new URLSearchParams(location.search || "");
    const mode = params.get("mode") || "login";
    const targetPath = location.pathname; // /dashboard or /security

    (async () => {
      try {
        if (mode === "link") {
          await linkGoogle(sessionId);
          setStatus("linked");
          // Clean URL and stay on profile/security page
          window.history.replaceState(null, "", targetPath);
          setTimeout(() => navigate(targetPath, { replace: true }), 700);
        } else {
          await exchangeGoogleSession(sessionId);
          setStatus("ok");
          // Clean URL fragment and route to dashboard
          window.history.replaceState(null, "", targetPath);
          setTimeout(() => navigate(targetPath, { replace: true }), 400);
        }
      } catch (e) {
        setError(e.message || "Authentication failed");
        setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-armada-base flex items-center justify-center px-6">
      <div className="absolute inset-0 dashboard-grid opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-[440px] glass-strong rounded-2xl p-10 text-center animate-fade-up">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
            {status === "processing" && <Loader2 className="w-7 h-7 text-[#D4AF37] animate-spin" />}
            {(status === "ok" || status === "linked") && <ShieldCheck className="w-7 h-7 text-emerald-400" />}
            {status === "error" && <AlertCircle className="w-7 h-7 text-red-400" />}
          </div>
        </div>
        <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">SOVEREIGN COMMAND</div>
        <h1 className="text-2xl font-black text-white">
          {status === "processing" && "Authenticating with Google…"}
          {status === "ok" && "Welcome aboard, Commander"}
          {status === "linked" && "Google account linked"}
          {status === "error" && "Authentication failed"}
        </h1>
        {error && <p className="text-sm text-red-300 mt-3 font-mono">{error}</p>}
      </div>
    </div>
  );
}
